/**
 * What sort of country this is, at any point on the map.
 *
 * Pure, and tested, because a biome is a *decision about a column* and every
 * one of the interesting failures is arithmetic: a desert with a lake in it,
 * a snowline that cuts a hill in half, a mountain range that ends in a cliff
 * because two biomes met and neither gave way.
 *
 * **Heights blend, blocks do not.** Ruggedness and lift are continuous
 * functions of the noise, so two kinds of country meet on a slope rather than
 * a step; what changes at the border is only what the ground is *made of*,
 * and a line where grass becomes sand is a thing deserts really have.
 */

import { fbm2 } from "./noise.js";

export type Biome = "meadow" | "mountain" | "winter" | "desert";

/** How high above the sea a peak keeps its snow. */
export const SNOW_LINE = 26;

/**
 * How warm it is here, 0 to 1.
 *
 * Low frequency, but not as low as it first was. A climate should take a
 * couple of minutes to walk out of - and at one period every four hundred
 * blocks, a three-hundred-block world fits inside *less than one period*, so
 * a whole map landed in one part of the field. Measured over two seeds, one
 * came out nine parts meadow and the other four parts mountain: not worlds
 * with countries in them, worlds that were each one country.
 *
 * A hundred and twenty gives three or four bands across this map, which is a
 * walk rather than a step.
 */
export function warmthAt(x: number, z: number, seed: number): number {
  return fbm2(x / 120, z / 120, seed + 9001, 3);
}

/**
 * How mountainous, 0 to 1, smoothed at both ends.
 *
 * Its own noise rather than a consequence of height: mountains should be able
 * to be cold or warm, and a range that only ever appeared where it was
 * already high would be a range that never began anywhere.
 *
 * Smoothed with a curve rather than a threshold so the foothills exist. The
 * first version was a step, and every range on the map was a wall.
 */
export function ruggedAt(x: number, z: number, seed: number): number {
  const raw = fbm2(x / 90, z / 90, seed + 7331, 4);
  const t = Math.min(1, Math.max(0, (raw - 0.44) / 0.26));
  return t * t * (3 - 2 * t);
}

/** How much this column is lifted above the ordinary run of the land. */
/**
 * How much this column is lifted above the ordinary run of the land.
 *
 * Forty, not fifty-two. The taller version put peaks through the ceiling of
 * the world on some seeds, and a mountain flattened off at the top is not a
 * mountain, it is a plateau nobody chose.
 */
export function liftAt(rugged: number): number {
  return rugged * 40;
}

/** How much relief the noise is allowed here: gentle in meadow, savage high up. */
export function roughAt(rugged: number): number {
  return 7 + rugged * 24;
}

/**
 * Which country this is.
 *
 * Mountain wins over climate, because standing on one you are on a mountain
 * whatever the weather; below that, temperature alone decides. There was a
 * wetness field as well, and a desert needed hot *and* dry - measured over a
 * whole world it produced not one desert, because the two fields rarely went
 * far enough in the right directions in the same place. A second dimension
 * that only ever subtracts is a second dimension for nothing.
 *
 * The numbers come from measuring what the noise actually does, not from
 * what 0 to 1 suggests it might: warmth sits between 0.18 and 0.90 with its
 * middle at 0.60, so "cold" is below 0.42 and "hot" is above 0.66, and those
 * are each about a fifth of the map. One seed in four had no desert at all
 * before the hot end came down those four hundredths.
 */
export function biomeFrom(rugged: number, warmth: number): Biome {
  if (rugged > 0.50) return "mountain";
  if (warmth < 0.42) return "winter";
  if (warmth > 0.66) return "desert";
  return "meadow";
}

export function biomeAt(x: number, z: number, seed: number): Biome {
  return biomeFrom(ruggedAt(x, z, seed), warmthAt(x, z, seed));
}

/**
 * How likely a tree is here, against the meadow's own rate.
 *
 * Nothing in a desert; little on a mountain, and what there is stops at the
 * treeline; a winter is thinner than a meadow rather than bare, because a
 * wood with snow on it is the thing worth walking into.
 */
export function treeChance(biome: Biome, above: number): number {
  if (biome === "desert") return 0;
  if (biome === "mountain") return above > SNOW_LINE ? 0 : 0.35;
  if (biome === "winter") return 0.6;
  return 1;
}
